import * as validationLib from "../../lib/validation";
import GreenCheck from "../icons/GreenCheck";
import React, {useState} from "react";
import RedX from "../icons/RedX";
import Spinner from "../Spinner";
import cn from "../../lib/classnames";
import styles from "./LinedInput.module.css";
import textStyles from "../../styles/Text.module.css";
import Toggletip from "../Toggletip";
import {isValidInput} from "../../lib/isValidInput";

export enum InputType {
    Text = "text",
    Email = "email",
    Password = "password",
    Number = "number",
}

type Props = React.ComponentProps<"input"> & {
    id: string;
    value: string;
    setValue?: (s: string) => void;
    label: string;
    extraInfo?: string;
    type: InputType;
    validationMessage?: string[];
    showValidIcon?: boolean;
    onFocus?: ((event: React.FocusEvent<HTMLInputElement>) => void) | undefined;
    loading?: boolean;
    toggleText?: string;
};

function LinedInput(
    {
        className,
        id,
        value,
        setValue,
        label,
        extraInfo,
        validationMessage,
        readOnly,
        required,
        onBlur,
        showValidIcon = false,
        type,
        onChange,
        onFocus,
        loading,
        toggleText,
        ...rest
    }: Props,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    const [focused, setFocused] = useState(false);
    const [hasBlurred, setHasBlurred] = useState(false);

    const hasValidationMsg = !!validationMessage && validationMessage.length > 0;

    function onChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
        if (isValidInput(e.target.value) && onChange) {
            onChange(e);
        }
    }

    function onSetValue(e: React.ChangeEvent<HTMLInputElement>) {
        if (isValidInput(e.target.value) && setValue) {
            setValue(e.target.value);
        }
    }

    return (
        <div className={cn(styles.container, className)}>
            <input
                ref={ref}
                className={cn(
                    textStyles.input,
                    styles.input,
                    toggleText ? styles.inputWithToggle : styles.inputNoToggle,
                    hasValidationMsg ? styles.invalidInput : styles.validInput,
                    readOnly ? styles.readOnly : undefined,
                    type === InputType.Number ? styles.numberInput : undefined,
                )}
                id={id}
                value={value}
                onChange={onChange ? onChangeInput : onSetValue}
                placeholder={label}
                onFocus={(e) => {
                    if (onFocus) {
                        onFocus(e);
                    }
                    setFocused(true);
                }}
                onBlur={(e) => {
                    setFocused(false);
                    setHasBlurred(true);
                    if (onBlur) {
                        onBlur(e);
                    }
                }}
                readOnly={readOnly}
                disabled={readOnly}
                required={required}
                type={type}
                {...rest}
            />
            <label className={styles.label} htmlFor={id} data-content={label}>
                <span className={styles.hidden}>{label}</span>
            </label>

            {toggleText ? (
                <div className={styles.toggleTip}>
                    <Toggletip text={toggleText} />
                </div>
            ) : null}

            <Spinner className={styles.spinner} isVisible={loading} />

            <RedX
                className={cn(
                    toggleText ? styles.validityIconWithToggle : styles.validityIcon,
                    hasValidationMsg && !loading ? styles.showInvalid : styles.hideInvalid,
                )}
            />
            <GreenCheck
                className={cn(
                    toggleText ? styles.validityIconWithToggle : styles.validityIcon,
                    hasValidationMsg || (!required && !value && !hasBlurred) || loading
                        ? undefined
                        : styles.showValid,
                    showValidIcon ? styles.showValidAlways : undefined,
                )}
            />
            {validationMessage && validationMessage.length ? (
                <div className={cn(textStyles.inputError, styles.validation)}>
                    {validationLib.getDisplayString(validationMessage)}
                </div>
            ) : null}
            {extraInfo ? (
                <div
                    className={cn(
                        textStyles.inputDetail,
                        styles.extraInfo,
                        focused ? styles.expanded : styles.collapsed,
                    )}
                >
                    {extraInfo}
                </div>
            ) : null}
        </div>
    );
}

export default React.forwardRef<HTMLInputElement, Props>(LinedInput);
